import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";

import BingoBall from "./BingoBall";
import { sync } from "@/core/sync";
import { BINGO_EVENTS, makeEvent } from "@/core/sync/events";
import { useBingoSettingsStore } from "@/plugins/bingo/state/bingoSettingsStore";
import {
  SPECIAL_NUMBERS,
} from "@/plugins/bingo/projector/effects/specialFxConfig";

const LOCAL_EVENT = "ic-preview-reveal";

/**
 * triggerPreviewReveal — public helper. Fires a preview reveal on:
 *   • the current window (via a synchronous CustomEvent), and
 *   • all other open windows (via BroadcastChannel).
 *
 * BroadcastChannel does NOT loop back to the publishing tab, so we
 * dispatch a DOM CustomEvent locally to guarantee the overlay opens
 * on the same window the host clicked from.
 */
export function triggerPreviewReveal(explicitValue) {
  const value =
    typeof explicitValue === "number" ? explicitValue : pickDemoNumber();
  const payload = { value, ts: Date.now() };
  sync.publish(makeEvent(BINGO_EVENTS.PREVIEW_REVEAL, payload, "bingo"));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(LOCAL_EVENT, { detail: payload }));
  }
  return value;
}

/**
 * PreviewRevealOverlay — a rehearsal-only overlay that plays a full
 * standard-ball reveal sequence (ball + spin + open + reveal + magic
 * trail + cell light) using the CURRENT reveal-timing settings, without
 * touching the real game.
 *
 * Mounted at the App root so it's available on Home, Host, Projector,
 * and Settings. Any window can publish BINGO_EVENTS.PREVIEW_REVEAL with
 * { value, ts } and every open window animates the same demo number.
 *
 * The overlay renders its own compact 90-cell "demo board" so the trail
 * has a real target — completely separate from any live game board.
 * The value picked is guaranteed to be a NON-special standard number.
 */
export default function PreviewRevealOverlay() {
  const [demo, setDemo] = useState(null); // { value, ts, cellLit }
  const timing = useBingoSettingsStore((s) => s.settings.revealTiming);
  const dismissTimerRef = useRef(null);

  // Listen for PREVIEW_REVEAL on the shared BroadcastChannel AND for a
  // local CustomEvent (BroadcastChannel doesn't loop back to the same tab
  // that published, so the local trigger is what makes the overlay open
  // in the window where the host clicks "Preview Animation").
  useEffect(() => {
    const handlePayload = (payload) => {
      if (typeof payload?.ts === "number") {
        if (Date.now() - payload.ts > 5000) return;
      }
      const value = payload?.value;
      if (typeof value !== "number") return;
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      setDemo({ value, ts: payload.ts, cellLit: false });
    };

    const offSync = sync.subscribe((event) => {
      if (
        event.pluginId === "bingo" &&
        event.type === BINGO_EVENTS.PREVIEW_REVEAL
      ) {
        handlePayload(event.payload);
      }
    });

    const localHandler = (e) => handlePayload(e.detail);
    window.addEventListener(LOCAL_EVENT, localHandler);

    return () => {
      offSync();
      window.removeEventListener(LOCAL_EVENT, localHandler);
    };
  }, []);

  // Auto-dismiss the overlay 1.5s after the trail lands.
  const handleCellLightReady = () => {
    setDemo((d) => (d ? { ...d, cellLit: true } : d));
    dismissTimerRef.current = window.setTimeout(() => {
      setDemo(null);
      dismissTimerRef.current = null;
    }, 1500);
  };

  const dismiss = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setDemo(null);
  };

  return (
    <AnimatePresence>
      {demo && (
        <motion.div
          data-testid="preview-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center px-8"
          style={{
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(18px)",
          }}
        >
          <div className="w-full max-w-6xl flex flex-col items-center">
            <PreviewHeader onDismiss={dismiss} timing={timing} />

            {/* Ball */}
            <div className="mt-6 mb-8">
              <BingoBall
                key={`preview-ball-${demo.ts}`}
                value={demo.value}
                testidPrefix="preview"
                onCellLightReady={handleCellLightReady}
              />
            </div>

            {/* Demo board — trail targets the cell inside THIS grid. */}
            <PreviewDemoBoard
              value={demo.value}
              cellLit={demo.cellLit}
              pulseMs={timing.boardPulseMs}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PreviewHeader({ onDismiss, timing }) {
  const total =
    timing.ballAppearMs +
    timing.ballSpinMs +
    timing.ballOpenMs +
    timing.revealPauseMs +
    timing.magicTrailMs;
  return (
    <div className="w-full flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center gap-2 h-8 px-3 rounded-full text-[10px] tracking-[0.35em] uppercase"
          style={{
            background:
              "color-mix(in srgb, var(--ic-primary) 20%, transparent)",
            color: "var(--ic-primary)",
            border:
              "1px solid color-mix(in srgb, var(--ic-primary) 60%, transparent)",
          }}
        >
          <Sparkles className="w-3 h-3" />
          Preview · not saved to game
        </span>
        <span
          className="text-xs tracking-widest uppercase"
          style={{ color: "var(--ic-text-muted)" }}
        >
          {(total / 1000).toFixed(2)}s total
        </span>
      </div>
      <button
        data-testid="preview-dismiss"
        type="button"
        onClick={onDismiss}
        className="w-9 h-9 rounded-md inline-flex items-center justify-center border hover:brightness-125"
        style={{
          borderColor: "var(--ic-border-subtle)",
          background: "var(--ic-bg-elevated)",
          color: "var(--ic-text-secondary)",
        }}
        aria-label="Close preview"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * PreviewDemoBoard — a compact 10×9 grid used only as a MagicTrail
 * target during the preview. Cells have testids `preview-cell-{n}` so
 * they never collide with the live Projector or Host board.
 */
function PreviewDemoBoard({ value, cellLit, pulseMs }) {
  const pulseSec = pulseMs / 1000;
  const cells = useMemo(() => Array.from({ length: 90 }, (_, i) => i + 1), []);
  return (
    <div
      data-testid="preview-board"
      className="grid gap-[6px] w-full"
      style={{
        gridTemplateColumns: "repeat(10, minmax(0, 1fr))",
        maxWidth: "min(880px, 88vw)",
      }}
    >
      {cells.map((v) => {
        const isTarget = v === value;
        const isLit = isTarget && cellLit;
        return (
          <motion.div
            key={v}
            data-testid={`preview-cell-${v}`}
            initial={false}
            animate={
              isLit
                ? {
                    scale: [0.95, 1.22, 1.06],
                    transition: { duration: pulseSec, ease: "easeOut" },
                  }
                : { scale: 1 }
            }
            className="ic-font-display rounded-md flex items-center justify-center font-semibold select-none"
            style={{
              aspectRatio: "1 / 1",
              fontSize: "clamp(0.75rem, 1vw, 1.1rem)",
              ...(isLit
                ? {
                    background:
                      "color-mix(in srgb, var(--ic-primary) 26%, transparent)",
                    border:
                      "1.5px solid color-mix(in srgb, var(--ic-primary) 70%, transparent)",
                    color: "#ffffff",
                    boxShadow:
                      "0 0 22px var(--ic-primary-glow), inset 0 0 10px var(--ic-primary-glow)",
                  }
                : {
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid var(--ic-border-subtle)",
                    color: "var(--ic-text-muted)",
                  }),
            }}
          >
            {v}
          </motion.div>
        );
      })}
    </div>
  );
}

/**
 * pickDemoNumber — pick a random 1..90 that is NOT one of the special
 * numbers (Phase 1 legacy OR Phase 2 signature-triggering), so the
 * default Preview Animation always exercises the pure standard sequence.
 * Module-specific previews go through the module's own previewValue().
 */
export function pickDemoNumber() {
  const specials = new Set(Object.keys(SPECIAL_NUMBERS).map(Number));
  // Also exclude Phase 2 signature-triggering values so the standard-
  // reveal preview isn't accidentally decorated.
  const phase2 = [11, 22, 33, 44, 55, 66, 67, 77, 88];
  phase2.forEach((n) => specials.add(n));
  let n;
  let attempts = 0;
  do {
    n = 1 + Math.floor(Math.random() * 90);
    attempts += 1;
  } while (specials.has(n) && attempts < 40);
  return n;
}
